import { useEffect, useState } from 'react';
import './Record.css';
import type { RecordStats } from '../../shared/types.ts';
import { fetchStats } from '../lib/calls.ts';
import { formatCallTimestamp } from '../lib/formatting.ts';
import { JEFF } from '../config/jeff.ts';
import { useDocumentTitle } from '../lib/hooks.ts';

/**
 * Jeff's track record.
 *
 * The important thing this page does is *not* claim a win rate. Nothing
 * resolves calls against the market yet, so every call is PENDING and any
 * percentage would be invented. The schema, the aggregation and this layout all
 * support resolution the day it exists; until then the page shows volume and
 * says why the rest is blank.
 */
export function Record() {
  const [stats, setStats] = useState<RecordStats | null>(null);
  const [failed, setFailed] = useState(false);

  useDocumentTitle(`Track record — ${JEFF.appName}`);

  useEffect(() => {
    const controller = new AbortController();
    void fetchStats(controller.signal)
      .then((next) => !controller.signal.aborted && setStats(next))
      .catch(() => !controller.signal.aborted && setFailed(true));
    return () => controller.abort();
  }, []);

  return (
    <main className="record" id="main">
      <header className="record__head">
        <h1 className="record__title">JEFF&rsquo;S TRACK RECORD</h1>
        <p className="record__sub">
          {stats?.global ? 'All calls, all users.' : 'Calls made in this browser.'}
        </p>
      </header>

      {failed && <p className="record__error">Could not load the record.</p>}

      <dl className="record__metrics">
        <Metric label="Total calls" value={stats ? String(stats.totalCalls) : '—'} />
        <Metric label="Long calls" value={stats ? String(stats.longCalls) : '—'} tone="long" />
        <Metric label="Short calls" value={stats ? String(stats.shortCalls) : '—'} tone="short" />
      </dl>

      {stats && stats.totalCalls > 0 && (
        <div className="record__split" aria-hidden="true">
          <span
            className="record__split-long"
            style={{ flexGrow: Math.max(stats.longCalls, 0.001) }}
          />
          <span
            className="record__split-short"
            style={{ flexGrow: Math.max(stats.shortCalls, 0.001) }}
          />
        </div>
      )}

      <section className="record__block panel">
        <h2 className="record__block-title">Resolution</h2>
        {stats?.resolved ? (
          <dl className="record__metrics record__metrics--tight">
            <Metric
              label="Jeff win rate"
              value={
                stats.resolved.winRate === null
                  ? '—'
                  : `${Math.round(stats.resolved.winRate * 1000) / 10}%`
              }
            />
            <Metric label="Wins" value={String(stats.resolved.wins)} tone="long" />
            <Metric label="Losses" value={String(stats.resolved.losses)} tone="short" />
            {/*
              Shown so the numbers visibly add up. A call is voided when nobody
              can honestly grade it — the market never moved, the ticker could not
              be priced, or it was made before entry prices were recorded at all.
              Without this the page reads "0 wins, 0 losses" over a hundred calls
              and looks broken rather than honest.
            */}
            <Metric label="Voided" value={String(stats.resolved.voids)} />
            <Metric label="Current streak" value={formatStreak(stats.resolved.currentStreak)} />
            <Metric label="Longest win streak" value={String(stats.resolved.longestWinStreak)} />
            <Metric label="Pending" value={String(stats.resolved.pending)} />
          </dl>
        ) : (
          <p className="record__note">
            No win rate, because no call has been settled against the market yet. Nothing here
            will ever be estimated.
          </p>
        )}
      </section>

      {stats && (stats.firstCallAt || stats.lastCallAt) && (
        <dl className="record__range">
          {stats.firstCallAt && (
            <div>
              <dt>First call</dt>
              <dd>{formatCallTimestamp(stats.firstCallAt)}</dd>
            </div>
          )}
          {stats.lastCallAt && (
            <div>
              <dt>Latest call</dt>
              <dd>{formatCallTimestamp(stats.lastCallAt)}</dd>
            </div>
          )}
        </dl>
      )}

      <p className="record__foot">{JEFF.disclaimer}</p>
    </main>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: 'long' | 'short' }) {
  return (
    <div className={`metric${tone ? ` metric--${tone}` : ''}`}>
      <dt className="metric__label">{label}</dt>
      <dd className="metric__value">{value}</dd>
    </div>
  );
}

function formatStreak(streak: number): string {
  if (streak === 0) return '—';
  return streak > 0 ? `${streak} W` : `${Math.abs(streak)} L`;
}
